var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { OperationEvent } from '@augloop/tracelogger-schemas';
import { now } from './now';
var Operation = /** @class */ (function (_super) {
    __extends(Operation, _super);
    /**
     * Creates a new `Operation` object that can be logged
     * @param properties dynamic event details
     * @param clientMetadata the client application that requested this opperation
     * @param userContext details about the logged-in user
     */
    function Operation(properties, clientMetadata, userContext) {
        var _this = _super.call(this, properties) || this;
        _this.eventName = 'Operation';
        _this.metrics = {};
        if (!properties || properties.count == undefined) {
            _this.count = 1;
        }
        _this.setClientMetadata(clientMetadata);
        _this.setUserContext(userContext);
        if (properties === null || properties === void 0 ? void 0 : properties.operationName) {
            _this.setMetricsForOpName(properties.operationName);
        }
        return _this;
    }
    /**
     * Attaches metadata about the client-app that requested this operation
     * @param clientMetadata the client application that requested this operation
     * @returns `this` for fluent API
     */
    Operation.prototype.setClientMetadata = function (clientMetadata) {
        if (clientMetadata) {
            this.clientAppName = clientMetadata.appName;
            this.clientAppPlatform = clientMetadata.appPlatform;
            this.clientAppVersion = clientMetadata.appVersion;
            this.clientFlights = clientMetadata.flights;
            this.clientReleaseAudienceGroup = clientMetadata.releaseAudienceGroup;
            this.clientReleaseChannel = clientMetadata.releaseChannel;
            this.clientReleaseFork = clientMetadata.releaseFork;
            this.clientRuntimeVersion = clientMetadata.runtimeVersion;
            this.clientSessionId = clientMetadata.sessionId;
            this.clientDeviceId = clientMetadata.deviceId;
            this.clientUserAgent = clientMetadata.userAgent;
        }
        return this;
    };
    /**
     * Attaches metadata about the authenticated user that is using the system
     * @param userContext data about the authenticated user
     * @returns `this` for fluent API
     */
    Operation.prototype.setUserContext = function (userContext) {
        if (userContext) {
            // please ensure fields assigned here match those in userContextFieldNames in geneva-trace-logger.ts
            this.userId = userContext.puid || userContext.oid;
            this.userType = userContext.userType && userContext.userType.toString();
        }
        return this;
    };
    /**
     * Marks the start of this operation.
     * @note `durationMs` is not defined at this point.
     * @returns `this` for fluent API
     */
    Operation.prototype.start = function () {
        this.startTime = now();
        return this;
    };
    /**
     * Calculates and sets Operation `durationMs` since `start()` was called
     * @returns `this` for fluent API
     */
    Operation.prototype.stop = function () {
        var stopTime = now();
        this.durationMs = Math.round(stopTime - this.startTime);
        return this;
    };
    Operation.prototype.setMetricsForOpName = function (operationName) {
        var _this = this;
        switch (operationName) {
            case 'SessionHealthOrphanedEventsWithoutProperSessionKey':
            case 'SessionHealthOrphanedSessions':
            case 'WorkflowActivationState':
                this.metrics[operationName + '.CountV2'] = {
                    dimensionNames: this.getOrphanedSessionHealthDimensionNames.bind(this),
                    dimensionValues: this.getOrphanedSessionHealthDimensionValues.bind(this),
                    value: function () { return _this.count; }
                };
                break;
            case 'MarkUnhealthySession':
            case 'MarkHealthWarningSession':
                this.metrics[operationName + '.Reason'] = {
                    dimensionNames: this.getSessionHealthDimensionNames.bind(this),
                    dimensionValues: this.getSessionHealthDimensionValues.bind(this),
                    value: function () { return 1; }
                };
                break;
            default:
                if (operationName === 'matchmaker_timer') {
                    this.metrics['Workflow.DurationMs'] = {
                        dimensionNames: this.getWorkflowDimensionNames.bind(this),
                        dimensionValues: this.getWorkflowDimensionValues.bind(this),
                        value: function () { return _this.durationMs; }
                    };
                    this.metrics['Workflow.Count'] = {
                        dimensionNames: this.getWorkflowDimensionNames.bind(this),
                        dimensionValues: this.getWorkflowDimensionValues.bind(this),
                        value: function () { return 1; }
                    };
                }
                this.metrics[operationName + ".DurationMsV2"] = {
                    dimensionNames: this.getDimensionNames.bind(this),
                    dimensionValues: this.getDimensionValues.bind(this),
                    value: function () { return _this.durationMs; }
                };
                this.metrics[operationName + ".CountV2"] = {
                    dimensionNames: this.getDimensionNames.bind(this),
                    dimensionValues: this.getDimensionValues.bind(this),
                    value: function () { return _this.count; }
                };
        }
    };
    Operation.prototype.getDimensionNames = function () {
        return [
            'Success', 'ClientAppName', 'ClientAppPlatform', 'ResourceId',
            'Dimension0', 'Dimension1', 'Dimension2', 'Dimension3',
        ];
    };
    Operation.prototype.getDimensionValues = function () {
        return [
            this.success, this.clientAppName, this.clientAppPlatform, this.resourceId,
            this.dimension0, this.dimension1, this.dimension2, this.dimension3,
        ];
    };
    Operation.prototype.getOrphanedSessionHealthDimensionNames = function () {
        return [
            'Success', 'ClientAppName', 'ClientAppPlatform',
            'ClientReleaseAudienceGroup', 'ResourceId',
            'Dimension0', 'Dimension1', 'Dimension2', 'Dimension3',
        ];
    };
    Operation.prototype.getOrphanedSessionHealthDimensionValues = function () {
        return [
            this.success, this.clientAppName, this.clientAppPlatform,
            this.clientReleaseAudienceGroup, this.resourceId,
            this.dimension0, this.dimension1, this.dimension2, this.dimension3,
        ];
    };
    Operation.prototype.getSessionHealthDimensionNames = function () {
        return [
            'Reason',
            'ClientAppName', 'ClientAppPlatform', 'ClientAppVersion',
            'Dimension0', 'Dimension1', 'Dimension2', 'Dimension3',
        ];
    };
    Operation.prototype.getSessionHealthDimensionValues = function () {
        return [
            this.resultSignature,
            this.clientAppName, this.clientAppPlatform, this.clientAppVersion,
            this.dimension0, this.dimension1, this.dimension2, this.dimension3,
        ];
    };
    Operation.prototype.getWorkflowDimensionNames = function () {
        return [
            'ResultSignature', 'WorkflowId', 'ResourceId', 'Success',
        ];
    };
    Operation.prototype.getWorkflowDimensionValues = function () {
        return [
            this.resultSignature, this.resourceId, this.resourceId, this.success,
        ];
    };
    return Operation;
}(OperationEvent));
export { Operation };
//# sourceMappingURL=operation.js.map